/**
 * LogPage — Playtesting Log + Game Dev Log
 *
 * Per user iter 66, both pages now load real data from the backend:
 *  - /api/logs/playtest → playtest_log.xlsx
 *  - /api/logs/gamedev  → gamedev_log.xlsx
 *
 * The data is rendered as a clean tabular block under the page
 * heading.  Long multi-line "Notes" cells preserve their bullet
 * markers and line breaks (rendered with `whiteSpace: pre-wrap`).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import TopBar from "@/components/TopBar";
import NavPane from "@/components/NavPane";

const API = process.env.REACT_APP_BACKEND_URL;

const PAL = {
  bg: "#0F0E0C",
  ink: "#F5F1E8",
  muted: "#9C8E72",
  accent: "#E5C870",
  panel: "rgba(20,18,15,0.72)",
  border: "rgba(229,200,112,0.18)",
  rowAlt: "rgba(229,200,112,0.04)",
};
// Per user iter 62: log pages match the homepage typography.
const TIMES = "'Times New Roman',Times,serif";

function formatCell(v) {
  if (v === null || v === undefined || v === "") return "—";
  return String(v);
}

export default function LogPage({ kind }) {
  const isPlay = kind === "playtesting";
  const heading = isPlay ? "Playtesting Log" : "Game Dev Log";
  const blurb = isPlay
    ? "A running journal of playtests \u2014 who played, what broke, what surprised us, and what changed because of it."
    : "A working notebook for the studio \u2014 the design choices made, the prototypes burned, and the systems that survived.";
  const testRoot = isPlay ? "playtesting-log" : "gamedev-log";
  const apiPath = isPlay ? "/api/logs/playtest" : "/api/logs/gamedev";

  const [data, setData] = useState({ headers: [], rows: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios
      .get(`${API}${apiPath}`)
      .then((res) => {
        if (cancelled) return;
        setData({
          headers: Array.isArray(res.data?.headers) ? res.data.headers : [],
          rows: Array.isArray(res.data?.rows) ? res.data.rows : [],
        });
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.message || "load_failed");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [apiPath]);

  // Column widths — first column gets generous space, last column
  // (often "Notes for the resume") gets the most so multi-line bullet
  // lists can breathe.
  const headers = data.headers;
  const rows = data.rows;
  const colCount = headers.length || 1;
  const tplCols = headers.map((h, i) => {
    const lower = String(h || "").toLowerCase();
    if (lower.includes("note") || lower.includes("experience")) return "minmax(320px, 2.4fr)";
    if (lower === "title") return "minmax(220px, 1.4fr)";
    if (lower === "date") return "8.5rem";
    if (lower === "minutes" || lower === "hours") return "5rem";
    return "minmax(120px, 1fr)";
  });
  const gridTpl = tplCols.join(" ") || "1fr";

  return (
    <div className="min-h-screen" style={{ background: PAL.bg, color: PAL.ink }} data-testid={`${testRoot}-page`}>
      <TopBar theme="studio" />
      <div className="relative z-10 max-w-7xl mx-auto flex flex-col md:flex-row">
        <div className="md:w-[260px] md:shrink-0 md:min-h-screen flex flex-col pt-4 md:pt-6 px-4 md:px-6 pb-6 md:border-r" style={{ borderColor: PAL.border }}>
          <NavPane theme="studio" />
        </div>
        <main className="flex-1 flex flex-col pt-4 md:pt-6 px-6 md:px-12 pb-10">
          <div className="max-w-6xl">
            <h1
              className="leading-[1.02] mb-7"
              style={{
                fontFamily: TIMES,
                fontWeight: 500,
                color: PAL.accent,
                fontSize: "clamp(2.4rem, 5vw, 5rem)",
                letterSpacing: "0.005em",
                textShadow: "0 2px 0 rgba(0,0,0,0.55), 0 0 28px rgba(229,200,112,0.22)",
              }}
              data-testid={`${testRoot}-title`}
            >
              {heading}
            </h1>
            <p
              className="max-w-3xl mb-8 text-base"
              style={{ fontFamily: TIMES, color: "#BAB09A", lineHeight: 1.65, fontWeight: 400 }}
              data-testid={`${testRoot}-blurb`}
            >
              {blurb}
            </p>

            <div
              className="rounded-sm border overflow-hidden"
              style={{ background: PAL.panel, borderColor: PAL.border, fontFamily: TIMES }}
              data-testid={`${testRoot}-body`}
            >
              {loading && (
                <div className="p-6 text-base italic" style={{ color: PAL.muted }} data-testid={`${testRoot}-loading`}>
                  Loading the proprietor&rsquo;s ledger&hellip;
                </div>
              )}
              {error && (
                <div className="p-6 text-base" style={{ color: "#FF8B8B" }} data-testid={`${testRoot}-error`}>
                  Could not load the ledger ({error}).
                </div>
              )}
              {!loading && !error && rows.length === 0 && (
                <div className="p-6 text-base italic" style={{ color: PAL.muted }} data-testid={`${testRoot}-empty`}>
                  The first entry has not yet been pinned to this wall.
                </div>
              )}
              {!loading && !error && rows.length > 0 && (
                <div data-testid={`${testRoot}-table`} style={{ minWidth: 0 }}>
                  {/* Header row */}
                  <div
                    className="grid items-end gap-x-5 px-5 py-3 text-[11px] tracking-[0.25em] uppercase"
                    style={{
                      gridTemplateColumns: gridTpl,
                      color: PAL.accent,
                      borderBottom: `1px solid ${PAL.border}`,
                      fontWeight: 500,
                    }}
                  >
                    {headers.map((h, i) => (
                      <div key={i}>{h ?? ""}</div>
                    ))}
                  </div>
                  {/* Body rows */}
                  {rows.map((row, ri) => (
                    <div
                      key={ri}
                      className="grid items-start gap-x-5 px-5 py-4 text-[15px]"
                      style={{
                        gridTemplateColumns: gridTpl,
                        background: ri % 2 === 1 ? PAL.rowAlt : "transparent",
                        borderTop: ri === 0 ? "none" : `1px solid ${PAL.border}`,
                        color: PAL.ink,
                        lineHeight: 1.55,
                        fontWeight: 400,
                      }}
                      data-testid={`${testRoot}-row-${ri}`}
                    >
                      {Array.from({ length: colCount }).map((_, ci) => {
                        const v = row[ci];
                        const isNotes = (() => {
                          const h = String(headers[ci] || "").toLowerCase();
                          return h.includes("note") || h.includes("experience");
                        })();
                        return (
                          <div
                            key={ci}
                            style={{
                              whiteSpace: isNotes ? "pre-wrap" : "normal",
                              color: ci === 0 ? PAL.ink : (v === null || v === undefined || v === "" ? PAL.muted : "#D9CDA8"),
                              fontWeight: ci === 0 ? 500 : 400,
                              fontStyle: ci === 0 ? "normal" : "normal",
                            }}
                          >
                            {formatCell(v)}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
